<script setup>
import {ref, reactive, computed, watch, onMounted} from 'vue';
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const changeUrl = route('partners_int.sub-account-password.change');
const postponeUrl = route('partners_int.sub-account-password.postpone');

const min = computed(() => portalContentAndDesignStore.subAccountPasswordValidationRules.min || 8);
const regex = computed(() => portalContentAndDesignStore.subAccountPasswordValidationRules.regex || {});

const currentPassword = ref('');
const newPassword = ref('');
const newPasswordConfirmation = ref('');
const submitting = ref(false);

const errors = reactive({
    current_password: false,
    new_password: false,
});

const rules = reactive({
    passwordLength: false,
});

onMounted(() => {
    _.forEach(regex.value, (value, key) => {
        rules[_.camelCase(value)] = false;
    });
});

const rulesNotSatisfied = computed(() => {
    return Object.keys(rules).some((key) => !rules[key]);
});

const cannotChange = computed(() => {
    return rulesNotSatisfied.value || submitting.value;
});

const regexEntries = computed(() => {
    return Object.entries(regex.value).map(([pattern, label]) => ({
        key: _.camelCase(label),
        label,
    }));
});

watch(newPassword, (password) => {
    errors.new_password = false;
    _.forEach(regex.value, (value, key) => {
        rules[_.camelCase(value)] = new RegExp(key).test(password);
    });
    rules.passwordLength = password.length >= min.value;
});

watch(currentPassword, () => {
    errors.current_password = false;
});

const postpone = () => {
    if (submitting.value) return;
    submitting.value = true;
    axios.post(postponeUrl).finally(() => {
        submitting.value = false;
        portalContentAndDesignStore.subAccountShouldChangePassword = false;
    });
};

const submit = () => {
    if (cannotChange.value || submitting.value) return;

    submitting.value = true;
    axios.post(changeUrl, {
        current_password: currentPassword.value,
        new_password: newPassword.value,
        new_password_confirmation: newPasswordConfirmation.value,
    }).then(() => {
        portalContentAndDesignStore.subAccountShouldChangePassword = false;
    }).catch((error) => {
        const responseErrors = _.get(error, 'response.data.errors', {});
        Object.keys(responseErrors).forEach((key) => {
            if (key in errors) {
                errors[key] = true;
            }
        });
    }).finally(() => {
        submitting.value = false;
    });
};
</script>

<template>
    <modal
        title="Change password"
        :show="true"
        size="small"
        name="sub_account_password_change"
        :close-modal-on-outside-click="false"
        :close-modal-on-esc-button="false"
        :show-cancel-button="false"
    >
        <template #close-button>
            <span></span>
        </template>

        <template #body>
            <div class="grid gap-y-4">
                <text-field
                    type="password"
                    name="current_password"
                    required="true"
                    field-id="sa_current_password"
                    placeholder="Current password"
                    :error="errors.current_password"
                    v-model="currentPassword"
                />

                <div>
                    <text-field
                        type="password"
                        required="true"
                        name="new_password"
                        field-id="sa_new_password"
                        placeholder="New password"
                        :error="errors.new_password"
                        v-model="newPassword"
                    />

                    <div class="grid grid-cols-2 gap-2 mt-4">
                        <div
                            v-for="entry in regexEntries"
                            :key="entry.key"
                            class="flex items-center"
                        >
                            <div
                                class="h-2 w-2 rounded-full mr-2"
                                :class="[rules[entry.key] ? 'bg-black' : 'bg-gray-400']"
                            ></div>
                            <span class="text-xs tracking-wide">{{ entry.label }}</span>
                        </div>
                        <div class="flex items-center">
                            <div
                                class="h-2 w-2 rounded-full mr-2"
                                :class="[rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                            ></div>
                            <span class="text-xs tracking-wide">{{ min }} characters minimum</span>
                        </div>
                    </div>
                </div>

                <text-field
                    type="password"
                    required="true"
                    name="new_password_confirmation"
                    placeholder="Confirm new password"
                    field-id="sa_new_password_confirmation"
                    v-model="newPasswordConfirmation"
                />
            </div>
        </template>

        <template #footer>
            <btn
                @click.prevent="postpone"
                type="light"
                :disabled="submitting"
            >Remind later</btn>
            <btn
                @click="submit"
                :disabled="cannotChange"
                :loading="submitting"
            >Change</btn>
        </template>
    </modal>
</template>
